import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { shape, items, maxLength, required, fromModel, when } from '@totalsoft/pure-validations';
import { dateIsGreaterThanOrEqualToCurrentDate } from 'utils/simple-validations/validations';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useTranslation } from 'react-i18next';
import { isDirty } from '@totalsoft/change-tracking';
import { Prompt } from 'react-router-dom';
import AttachmentComponent from './AttachmentComponent';

const validator = fromModel(attachment =>
  shape({
    validityDate: dateIsGreaterThanOrEqualToCurrentDate |> when(!!attachment.validityDate),
    attachmentComment: maxLength(255),
    properties: items(
      fromModel(property =>
        shape({
          value: required |> when(property.mandatory)
        })
      )
    )
  })
);

const AttachmentContainer = ({
  attachment,
  maxSize,
  availableTransitions,
  onSave,
  onChangeState,
  onDelete,
  onUploadFile,
  onDownloadFile,
  onOpenDocStateTransitionsModal,
  onOpenHistoryModal,
  loadPropertyDetails
}) => {
  const { t } = useTranslation();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({});
  const [validation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [lens, dirtyInfo, validate]);

  useEffect(() => {
    if (attachment && !attachment.actionInProgress && attachment.withLensUpdate) {
      resetLens(attachment);
    }
  }, [attachment, resetLens]);

  /* Handlers */

  const handleValidateAndSave = useCallback(() => {
    if (!validate(lens |> get)) {
      return;
    }
    onSave(lens |> get);
  }, [lens, onSave, validate]);

  /* End Handlers */

  return (
    <>
      {isDirty(dirtyInfo) && <Prompt message={t('LeavingWithoutSaving')} />}
      <AttachmentComponent
        attachment={lens}
        maxSize={maxSize}
        availableTransitions={availableTransitions}
        actionInProgress={attachment.actionInProgress}
        isDirty={isDirty(dirtyInfo)}
        onSave={handleValidateAndSave}
        onChangeState={onChangeState}
        onDelete={onDelete}
        onUploadFile={onUploadFile}
        onDownloadFile={onDownloadFile}
        onOpenDocStateTransitionsModal={onOpenDocStateTransitionsModal}
        onOpenHistoryModal={onOpenHistoryModal}
        loadPropertyDetails={loadPropertyDetails}
        validation={validation}
      />
    </>
  );
};

AttachmentContainer.propTypes = {
  attachment: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
  availableTransitions: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onChangeState: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUploadFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onOpenDocStateTransitionsModal: PropTypes.func.isRequired,
  onOpenHistoryModal: PropTypes.func.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired
};

export default AttachmentContainer;
